import { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { ToastItem } from './toast';
import { useToastProvider } from './toast-provider.hook';
import { toastProviderStyles } from './toast-provider.styles';
import { SHARED_TEST_IDS } from '@/utils';
import { TOAST } from './utils';
import type { TToastContextValue } from './toast.types';

const ToastContext = createContext<TToastContextValue | null>(null);

const ToastProvider = ({ children }: { children: React.ReactNode }) => {
	const value = useToastProvider();

	return (
		<ToastContext.Provider value={value}>
			{children}
			{typeof document !== 'undefined' && createPortal(
				<div
					className={toastProviderStyles.portal}
					aria-label={TOAST.PORTAL_ARIA}
					data-testid={SHARED_TEST_IDS.TOAST_PORTAL}
				>
					{value.toasts.map((toast) => (
						<ToastItem key={toast.id} toast={toast} onRemove={value.removeToast} />
					))}
				</div>,
				document.body
			)}
		</ToastContext.Provider>
	);
};

const useToast = (): TToastContextValue => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error('useToast must be used within a ToastProvider');
	}
	return context;
};

ToastProvider.displayName = 'ToastProvider';

export { ToastProvider, useToast };
